import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const BarsVisual = () => (
  <svg viewBox="0 0 320 200" className="h-full w-full" aria-hidden="true">
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <motion.rect key={i} x={30 + i * 46} width="26" rx="6"
        initial={{ height: 20, y: 160 }} whileInView={{ height: 40 + i * 22, y: 160 - (40 + i * 22) }}
        viewport={{ once: false }} transition={{ duration: 1, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
        fill={i % 2 ? "rgba(32,184,173,0.75)" : "rgba(118,87,232,0.75)"} />
    ))}
    <line x1="20" y1="162" x2="310" y2="162" stroke="rgba(245,247,255,0.15)" strokeWidth="1.5" />
  </svg>
);

const LineVisual = () => (
  <svg viewBox="0 0 320 200" className="h-full w-full" aria-hidden="true">
    <motion.path d="M25 160 C70 150 90 90 135 100 S210 60 295 30" fill="none" stroke="#20B8AD" strokeWidth="3" strokeLinecap="round"
      initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: false }} transition={{ duration: 1.6, ease: "easeOut" }} />
    <motion.path d="M25 170 C80 168 120 140 165 142 S250 110 295 95" fill="none" stroke="#7657E8" strokeWidth="3" strokeLinecap="round" strokeDasharray="1 8"
      initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: false }} transition={{ duration: 1.6, delay: 0.3, ease: "easeOut" }} />
    {[["135", "100"], ["295", "30"]].map(([cx, cy], i) => (
      <motion.circle key={i} cx={cx} cy={cy} r="5" fill="#F5F7FF" initial={{ scale: 0 }} whileInView={{ scale: 1 }}
        viewport={{ once: false }} transition={{ delay: 0.9 + i * 0.3 }} />
    ))}
  </svg>
);

const NetworkVisual = () => (
  <svg viewBox="0 0 320 200" className="h-full w-full" aria-hidden="true">
    {[[60, 60, 160, 100], [160, 100, 260, 55], [160, 100, 250, 150], [60, 60, 90, 155], [90, 155, 250, 150]].map(([x1, y1, x2, y2], i) => (
      <motion.line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(118,87,232,0.5)" strokeWidth="1.5"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: false }} transition={{ duration: 0.8, delay: i * 0.15 }} />
    ))}
    {[[60, 60], [160, 100], [260, 55], [250, 150], [90, 155]].map(([cx, cy], i) => (
      <motion.circle key={i} cx={cx} cy={cy} r={i === 1 ? 12 : 7} fill={i === 1 ? "#7657E8" : "#20B8AD"}
        initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: false }} transition={{ delay: i * 0.12, type: "spring" }} />
    ))}
  </svg>
);

const ImageVisual = () => (
  <img src="https://images.pexels.com/photos/4089908/pexels-photo-4089908.jpeg?auto=compress&cs=tinysrgb&w=1200"
    alt="Travellers planning a journey together" className="h-full w-full rounded-2xl object-cover" />
);

const SLIDES = [
  { eyebrow: "AI & Technology", title: "Products that think.", copy: "AI-first platforms built around real workflows — from marketing intelligence to business communication.", visual: <BarsVisual />, to: "/ventures/mediaos", cta: "See Media OS" },
  { eyebrow: "Marketing Intelligence & Performance", title: "Decisions, not guesses.", copy: "Media planning, performance marketing and measurement built on evidence and economics.", visual: <LineVisual />, to: "/solutions", cta: "Explore Solutions" },
  { eyebrow: "Consumer Ventures", title: "Experiences worth remembering.", copy: "Travel, hospitality and everyday services designed with the same discipline as our technology.", visual: <ImageVisual />, to: "/ventures", cta: "View Ventures" },
  { eyebrow: "Affiliate & Strategic Partnerships", title: "Growth, shared.", copy: "CPS, CPA and CPL programs engineered for incrementality — with validation and honest reporting.", visual: <NetworkVisual />, to: "/affiliate", cta: "Partner With Us" },
];

export const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = useRef(typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  const next = useCallback(() => setIndex((i) => (i + 1) % SLIDES.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused || reduced.current) return undefined;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [paused, next]);

  const slide = SLIDES[index];

  return (
    <section data-testid="hero-carousel" aria-roledescription="carousel" aria-label="Ekavrix highlights"
      className="py-10 sm:py-14"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}
      onKeyDown={(e) => { if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); }}>
      <div className="container-ek">
        <div className="relative overflow-hidden rounded-[2rem] border border-white/[0.07] bg-deep">
          <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-violet/15 blur-[100px]" />
          <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-teal/10 blur-[100px]" />
          <AnimatePresence mode="wait">
            <motion.div key={index}
              initial={{ opacity: 0, x: 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="relative grid min-h-[380px] items-center gap-8 p-8 sm:p-12 lg:grid-cols-2">
              <div>
                <p className="eyebrow mb-4">{slide.eyebrow}</p>
                <h3 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-5xl">{slide.title}</h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">{slide.copy}</p>
                <Link to={slide.to} data-testid={`carousel-cta-${index}`} className="btn-primary mt-7 !px-6 !py-3 text-xs">
                  {slide.cta}
                </Link>
              </div>
              <div className="hidden h-[240px] lg:block">{slide.visual}</div>
            </motion.div>
          </AnimatePresence>
          <div className="absolute bottom-5 left-8 flex items-center gap-2 sm:left-12">
            {SLIDES.map((s, i) => (
              <button key={i} data-testid={`carousel-dot-${i}`} aria-label={`Go to slide ${i + 1}: ${s.eyebrow}`} aria-current={i === index}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-400 ${i === index ? "w-8 bg-teal" : "w-3 bg-white/20 hover:bg-white/40"}`} />
            ))}
          </div>
          <div className="absolute bottom-5 right-8 flex gap-2 sm:right-12">
            <button data-testid="carousel-prev" aria-label="Previous slide" onClick={prev}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/12 text-slate-300 transition-colors hover:border-teal/50 hover:text-white">
              <ChevronLeft size={16} />
            </button>
            <button data-testid="carousel-next" aria-label="Next slide" onClick={next}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/12 text-slate-300 transition-colors hover:border-teal/50 hover:text-white">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
